/**
 * 四门户 API client 工厂（FE-E2b 下沉）：admin/agent/merchant/customer 四端
 * src/api/client.ts 的公共核心在此单一实现，端差异全部经 CreateClientConfig 参数化。
 *
 * 行为规格 = FE-E2a 契约清单（四端 src/api/client.contract.test.ts 锚定）：
 * - 401 单飞刷新 / REFRESHABLE_CODES / 登出三分支 / 超时 / 追踪码 / 信封解析（四端一致）
 * - 幂等键矩阵（PUT 豁免=customer / DELETE 键=admin、merchant / 键生成回退形态）为参数
 * - errorFormat=code-prefix（customer `CODE: message`）/ includeStatus（agent US-201）为参数
 * - customHeaderMode：admin 过滤 Authorization / agent 不合并（现状锁定）/ 其余 spread
 * - 既有缺陷按现状锁定（见 FE-E2a 报告疑虑 Y2），工厂迁移不改行为
 *
 * 本模块不 import 任何门户代码（verify-p2-boundary：shared 零门户依赖）。
 */

// ===================== 公共类型 =====================

/** 工厂可识别的会话最小面（端会话类型的超集约束；端特有字段经泛型透传） */
interface ClientSessionBase {
  accessToken?: string;
  refreshToken?: string;
  [key: string]: unknown;
}

/** 后端 Result 信封：{ code, message, data, traceId }（traceId 供报障定位服务端日志） */
type BackendEnvelope<T> = {
  code?: string | number;
  message?: string;
  data?: T;
  traceId?: string;
};

/** 带状态码与业务 code 的原始结果（仅供内部 refresh 判定使用） */
export type RawResult<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; code?: string; error: string; traceId?: string };

/** 自定义 headers 合并策略（四端差异 D7）：admin 过滤 Authorization / agent 不合并 / 其余直并 */
type CustomHeaderMode = 'filter-auth' | 'spread' | 'ignore';

/** 错误信封 error 公式（四端差异 D8）：customer 为 `CODE: message` 前缀拼接 */
type ErrorFormat = 'message-first' | 'code-prefix';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOpts {
  skipAuth?: boolean;
  /** 同一次逻辑写请求（含 401 refresh 重放）必须复用同一幂等键。 */
  idempotencyKey?: string;
  /** 附加请求头；处理方式由 customHeaderMode 决定（不得由调用方伪造 Authorization）。 */
  headers?: Record<string, string>;
}

interface CreateClientConfig<S extends ClientSessionBase> {
  /** baseUrl 双态：dev 走 Vite proxy 相对路径；生产固定 Tunnel 绝对路径（尾斜杠统一清理） */
  apiBase: { dev: string; prod: string };
  /** sessionStorage 会话键（四端各异，迁移后保持不变） */
  sessionKey: string;
  /** 无注入 handler 时 signOutLocal 兜底硬跳目标（customer 为语言化函数） */
  loginPath: string | (() => string);
  /** 端特有身份头（admin X-Operator-Id / customer X-Cardholder-Id 回退；agent/merchant 不传） */
  extraHeaders?: (s: S | null) => Record<string, string>;
  /** 自定义 headers 合并策略，缺省 'spread' */
  customHeaderMode?: CustomHeaderMode;
  /** 错误信封 error 公式，缺省 'message-first' */
  errorFormat?: ErrorFormat;
  /** 失败信封是否携带 status 字段（agent US-201 业务码 vs 网络异常分流） */
  includeStatus?: boolean;
  /** PUT 幂等豁免（customer US-PREF-2 / 合同 §3.7 A2）：严禁携带 Idempotency-Key */
  putIdempotencyExempt?: boolean;
  /** DELETE 是否注入 Idempotency-Key（admin/merchant 必填写接口；agent/customer 不注入） */
  deleteIdempotencyKey?: boolean;
  /** JSON 写路径幂等键回退形态：'ts' = idem-{Date.now()}；'ts-random36' 追加随机后缀 */
  jsonKeyGen?: 'ts' | 'ts-random36';
  /** multipart 上传幂等键回退形态（customer 上传为 'ts'，与 JSON 路径不同，保持原行为） */
  uploadKeyGen?: 'ts' | 'ts-random36';
  /** 业务失败回调（merchant 3417 PENDING 商户引导；失败分支返回前调用） */
  onBusinessError?: (code: string | undefined, error: string) => void;
}

// ===================== 工厂实现 =====================

export function createClient<S extends ClientSessionBase>(cfg: CreateClientConfig<S>) {
  const customHeaderMode = cfg.customHeaderMode ?? 'spread';
  const errorFormat = cfg.errorFormat ?? 'message-first';

  // ---- baseUrl（契约 C1：双态 + 尾斜杠清理 + 拼接归一） ----
  const API_BASE = (import.meta.env.PROD ? cfg.apiBase.prod : cfg.apiBase.dev).replace(/\/$/, '');

  function apiUrl(path: string): string {
    const p = path.startsWith('/') ? path : `/${path}`;
    return `${API_BASE}${p}`;
  }

  // ---- 会话存取（契约 C3：sessionStorage + JSON + 损坏容错 + 写后广播） ----
  const SESSION_KEY = cfg.sessionKey;
  /** 会话写入后广播，供 RequireSetup / Layout 重读 me 标志 */
  const SESSION_CHANGED_EVENT = 'allinks-session';

  function emitSessionChanged() {
    if (typeof window === 'undefined') {
      return;
    }
    window.dispatchEvent(new Event(SESSION_CHANGED_EVENT));
  }

  function loadSession(): S | null {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as S;
    } catch {
      return null;
    }
  }

  function saveSession(s: S) {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
    emitSessionChanged();
  }

  function clearSession() {
    sessionStorage.removeItem(SESSION_KEY);
    emitSessionChanged();
  }

  // ---- 401 可续期码与登出路径（契约 C4/C6） ----
  /** 可触发 refresh 的业务 code（accessToken 过期 / 缺失但会话存在） */
  const REFRESHABLE_CODES: ReadonlySet<string> = new Set(['AUTH_401', 'AUTH_401_X']);
  // 非过期类 401（AUTH_401_T 等）与未知 401 code：refresh 无意义，
  // 行为一致——统一走 signOutLocal（clearSession + 跳登录）。

  /** 模块级 refresh 去重 promise：并发 401 共享同一次 refresh（契约 C5 单飞） */
  let refreshPromise: Promise<boolean> | null = null;

  /** 由 App 注入的鉴权失败回调（navigate('/login')）；未注入时硬跳兜底 */
  let onAuthFailure: (() => void) | null = null;

  function setAuthFailureHandler(fn: (() => void) | null) {
    onAuthFailure = fn;
  }

  /** 清会话 + 跳登录：refresh 失败 / 非过期 401 / 主动登出均走此路径 */
  function signOutLocal() {
    clearSession();
    if (onAuthFailure) {
      onAuthFailure();
    } else if (typeof window !== 'undefined') {
      const target = typeof cfg.loginPath === 'function' ? cfg.loginPath() : cfg.loginPath;
      window.location.href = target;
    }
  }

  /** refresh 响应所需字段（结构化最小子集，避免与端 auth.ts 形成循环依赖） */
  type RefreshTokenPair = {
    accessToken: string;
    refreshToken: string;
    expiresIn?: number;
  };

  /** 执行一次 /auth/refresh；并发调用共享同一 promise（去重） */
  function performRefresh(): Promise<boolean> {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
      const s = loadSession();
      if (!s?.refreshToken) return false;
      const res = await rawRequest<RefreshTokenPair>(
        'POST',
        '/auth/refresh',
        { refreshToken: s.refreshToken },
        { skipAuth: true },
      );
      if (!res.ok) return false;
      saveSession({
        ...s,
        accessToken: res.data.accessToken,
        refreshToken: res.data.refreshToken,
        expiresAt: res.data.expiresIn ? Date.now() + res.data.expiresIn * 1000 : (s.expiresAt as number | undefined),
      });
      return true;
    })().finally(() => {
      refreshPromise = null;
    });
    return refreshPromise;
  }

  // ---- 超时与传输（契约 C7/C8） ----
  /** JSON 请求超时（ms）；超时 fetch 抛 TimeoutError，由端 apiErrorMessages 归类为「请求超时」文案 */
  const JSON_TIMEOUT_MS = 30_000;
  /** 上传/下载类超时（ms）：大文件（CSV 批量导入 / PDF 对账单）耗时更长 */
  const TRANSFER_TIMEOUT_MS = 120_000;

  /** AbortSignal.timeout 帮助函数：旧浏览器（无该 API）退化为不限时，保持既有行为 */
  function timeoutSignal(ms: number): AbortSignal | undefined {
    return typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
      ? AbortSignal.timeout(ms)
      : undefined;
  }

  // ---- 追踪码（契约 C9） ----
  /** 必须报障排查的系统级错误码：展示时附带追踪码，供用户报障后按码检索服务端日志 */
  const TRACE_REQUIRED_CODES: ReadonlySet<string> = new Set(['SYSTEM_ERROR', 'INTERNAL_ERROR']);

  /** 系统级错误附加追踪码（信封 traceId）；非系统码或无追踪码时原样返回 */
  function appendTraceHint(code: string | undefined, error: string, traceId: string | undefined): string {
    if (!traceId || !code || !TRACE_REQUIRED_CODES.has(code)) {
      return error;
    }
    return `${error}（追踪码 ${traceId}）`;
  }

  function isOkCode(code: string | number | undefined): boolean {
    return code === 0 || code === '0' || code === undefined;
  }

  // ---- 错误信封解析（契约 C10 + 差异 D8） ----
  /** customer 端 codeMsg：业务错误统一携带错误码 `CODE: message`（双有拼、缺单边取非空侧） */
  function codeMsg(code: string | number | undefined, message?: string): string {
    const c = code === undefined ? '' : String(code);
    const m = (message || '').trim();
    if (c && m) return `${c}: ${m}`;
    return c || m;
  }

  async function parseRaw<T>(res: Response): Promise<RawResult<T>> {
    let body: BackendEnvelope<T> | T | null = null;
    try {
      body = (await res.json()) as BackendEnvelope<T> | T;
    } catch {
      return {
        ok: false,
        status: res.status,
        error: res.ok ? 'invalid_json' : `HTTP ${res.status}`,
      };
    }
    const env =
      body && typeof body === 'object' && 'code' in (body as object)
        ? (body as BackendEnvelope<T>)
        : null;
    if (!res.ok) {
      const code = env?.code !== undefined ? String(env.code) : undefined;
      const error =
        errorFormat === 'code-prefix'
          ? codeMsg(env?.code, env?.message) || `HTTP ${res.status}`
          : env?.message || String(env?.code ?? res.status);
      return {
        ok: false,
        status: res.status,
        code,
        error: appendTraceHint(code, error, env?.traceId),
        traceId: env?.traceId,
      };
    }
    if (env && !isOkCode(env.code)) {
      const code = String(env.code);
      const error =
        errorFormat === 'code-prefix'
          ? codeMsg(env.code, env.message) || String(env.code)
          : env.message || String(env.code);
      return {
        ok: false,
        status: res.status,
        code,
        error: appendTraceHint(code, error, env.traceId),
        traceId: env.traceId,
      };
    }
    // 成功：信封取 data，裸响应取整包
    return { ok: true, data: (env ? env.data : body) as T };
  }

  // ---- 鉴权头（契约 C12 + 差异 D6/D7） ----
  function buildHeaders(skipAuth: boolean): Record<string, string> {
    const h: Record<string, string> = { Accept: 'application/json' };
    if (skipAuth) {
      return h;
    }
    const s = loadSession();
    if (s?.accessToken) {
      // 正式路径：主体由 JWT claim 注入，前端不伪造操作者角色头
      h.Authorization = `Bearer ${s.accessToken}`;
    }
    // 端特有身份头（admin X-Operator-Id / customer 无 token 回退 X-Cardholder-Id）
    const extra = cfg.extraHeaders?.(s);
    if (extra) {
      for (const [k, v] of Object.entries(extra)) {
        h[k] = v;
      }
    }
    return h;
  }

  /** 自定义 headers 按端策略合并进基础头（admin 过滤 Authorization / agent 不合并 / 其余直并） */
  function applyCustomHeaders(
    headers: Record<string, string>,
    custom?: Record<string, string>,
  ): Record<string, string> {
    if (!custom || Object.keys(custom).length === 0) {
      return headers;
    }
    if (customHeaderMode === 'ignore') {
      return headers;
    }
    const merged: Record<string, string> =
      customHeaderMode === 'filter-auth'
        ? Object.fromEntries(
            Object.entries(custom).filter(([name]) => name.toLowerCase() !== 'authorization'),
          )
        : { ...custom };
    return { ...headers, ...merged };
  }

  // ---- 幂等键矩阵（契约差异 D3/D4/D5） ----
  function keyGen(kind: 'ts' | 'ts-random36'): string {
    if (kind === 'ts') {
      return crypto.randomUUID?.() ?? `idem-${Date.now()}`;
    }
    return crypto.randomUUID?.() ?? `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  const jsonKeyKind = cfg.jsonKeyGen ?? 'ts-random36';
  const uploadKeyKind = cfg.uploadKeyGen ?? 'ts-random36';

  /** 该写方法是否注入 Idempotency-Key（PUT 豁免 / DELETE 端差异由此推导） */
  function methodNeedsKey(method: HttpMethod): boolean {
    if (method === 'POST') return true;
    if (method === 'PUT') return !cfg.putIdempotencyExempt;
    if (method === 'PATCH') return true;
    if (method === 'DELETE') return !!cfg.deleteIdempotencyKey;
    return false;
  }

  // ---- 底层请求（契约 C13 + 差异 D3） ----
  async function rawRequest<T>(
    method: HttpMethod,
    path: string,
    body: unknown,
    opts?: RequestOpts,
  ): Promise<RawResult<T>> {
    try {
      const headers = applyCustomHeaders(buildHeaders(!!opts?.skipAuth), opts?.headers);
      let payload: string | undefined;
      if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
        headers['Content-Type'] = 'application/json';
        if (methodNeedsKey(method)) {
          headers['Idempotency-Key'] = opts?.idempotencyKey ?? keyGen(jsonKeyKind);
        }
        payload = body == null ? undefined : JSON.stringify(body);
      } else if (method === 'DELETE') {
        // DELETE 无 body；注入键与否由 deleteIdempotencyKey 决定（admin/merchant 软删等写接口必填）
        if (methodNeedsKey(method)) {
          headers['Idempotency-Key'] = opts?.idempotencyKey ?? keyGen(jsonKeyKind);
        }
      }
      const res = await fetch(apiUrl(path), {
        method,
        headers,
        // 携带 HttpOnly allinks_otp_dev；Bearer JWT 仍走 Authorization
        credentials: 'include',
        body: payload,
        signal: timeoutSignal(JSON_TIMEOUT_MS),
      });
      return parseRaw<T>(res);
    } catch (e) {
      return { ok: false, status: 0, error: e instanceof Error ? e.message : 'network_error' };
    }
  }

  // ---- 401 自动 refresh 重放（契约 C11） ----
  /** 鉴权流式/写路径通用 401 重放器：可续期 → 单飞 refresh → 原参重放一次；否则登出 */
  async function retryOnceOn401<T>(run: () => Promise<RawResult<T>>): Promise<RawResult<T>> {
    let res = await run();
    if (!res.ok && res.status === 401) {
      if (res.code && REFRESHABLE_CODES.has(res.code) && await performRefresh()) {
        res = await run();
        if (!res.ok && res.status === 401) signOutLocal();
      } else {
        signOutLocal();
      }
    }
    return res;
  }

  function toApiResult<T>(res: RawResult<T>):
    | { ok: true; data: T }
    | { ok: false; error: string; code?: string; traceId?: string; status?: number } {
    if (res.ok) {
      return { ok: true, data: res.data };
    }
    if (!res.ok) {
      cfg.onBusinessError?.(res.code, res.error);
    }
    return cfg.includeStatus
      ? { ok: false, error: res.error, code: res.code, status: res.status, traceId: res.traceId }
      : { ok: false, error: res.error, code: res.code, traceId: res.traceId };
  }

  /**
   * 401 自动 refresh 重放封装（四门户镜像契约核心）：
   * - 仅对鉴权请求（skipAuth !== true）生效；login/refresh 自身直返错误
   * - HTTP 401 + code ∈ {AUTH_401, AUTH_401_X} → refresh（去重）→ 成功重放 1 次
   * - HTTP 401 + 非过期 code 或未知 code → 清会话跳登录
   */
  async function fetchWithRefresh<T>(
    method: HttpMethod,
    path: string,
    body: unknown,
    opts?: RequestOpts,
  ): Promise<ReturnType<typeof toApiResult<T>>> {
    const requestOpts: RequestOpts = methodNeedsKey(method)
      ? { ...opts, idempotencyKey: opts?.idempotencyKey ?? keyGen(jsonKeyKind) }
      : { ...opts };
    let res = await rawRequest<T>(method, path, body, requestOpts);
    if (!res.ok && res.status === 401 && !opts?.skipAuth) {
      const code = res.code;
      if (code && REFRESHABLE_CODES.has(code)) {
        // 可续期：去重 refresh，成功后最多重放一次（复用同一幂等键）
        const refreshed = await performRefresh();
        if (refreshed) {
          res = await rawRequest<T>(method, path, body, requestOpts);
          // 重放后仍 401：异常态，保守登出
          if (!res.ok && res.status === 401) {
            signOutLocal();
          }
        } else {
          signOutLocal();
        }
      } else {
        // 非过期类 401 或未知 401 code：refresh 无意义，直接登出
        signOutLocal();
      }
    }
    // 失败时透传后端业务 code，供上层统一分流（端可含 status：agent US-201）
    return toApiResult(res);
  }

  // ---- multipart 上传（401 重放复用同一幂等键，契约第 12 组） ----
  async function rawUpload<T>(
    path: string,
    formData: FormData,
    idempotencyKey: string,
    extraHeaders?: Record<string, string>,
  ): Promise<RawResult<T>> {
    try {
      const headers = applyCustomHeaders(buildHeaders(false), extraHeaders);
      headers['Idempotency-Key'] = idempotencyKey;
      const res = await fetch(apiUrl(path), {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData,
        signal: timeoutSignal(TRANSFER_TIMEOUT_MS),
      });
      return parseRaw<T>(res);
    } catch (e) {
      return { ok: false, status: 0, error: e instanceof Error ? e.message : 'network_error' };
    }
  }

  async function upload<T>(
    path: string,
    formData: FormData,
    opts?: RequestOpts,
  ): Promise<ReturnType<typeof toApiResult<T>>> {
    // refresh 重放必须复用同一幂等键，禁止一次逻辑上传产生两次业务写入。
    const idempotencyKey = opts?.idempotencyKey ?? keyGen(uploadKeyKind);
    const res = await retryOnceOn401(() =>
      rawUpload<T>(path, formData, idempotencyKey, opts?.headers),
    );
    return toApiResult(res);
  }

  return {
    apiUrl,
    apiBase: API_BASE,
    sessionKey: SESSION_KEY,
    SESSION_CHANGED_EVENT,
    loadSession,
    saveSession,
    clearSession,
    setAuthFailureHandler,
    signOutLocal,
    fetchWithRefresh,
    upload,
    // 内部件：端薄壳拼装门户特有流式路径（adminDownload / agentGetBlob 等 D10 差异项）
    internals: {
      buildHeaders,
      parseRaw,
      timeoutSignal,
      appendTraceHint,
      performRefresh,
      retryOnceOn401,
      REFRESHABLE_CODES,
      JSON_TIMEOUT_MS,
      TRANSFER_TIMEOUT_MS,
    },
  };
}
